import React, { createContext, useContext, useState, ReactNode } from 'react';
import { trackError } from '@/utils/error-tracking';
import { errorMessageOr } from '@/utils/error-message';

export interface Partner {
  id: number;
  name: string;
  membership_type: number;
  code: string;
}

interface PartnersDataContextType {
  partners: Partner[];
  loading: boolean;
  error: string | null;
  refreshPartners: () => Promise<void>;
  ensureLoaded: () => Promise<void>;
}

const PartnersDataContext = createContext<PartnersDataContextType | undefined>(undefined);

interface PartnersDataProviderProps {
  children: ReactNode;
  initialPartners?: Partner[];
}

export const PartnersDataProvider: React.FC<PartnersDataProviderProps> = ({
  children,
  initialPartners,
}) => {
  const [partners, setPartners] = useState<Partner[]>(initialPartners || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(!!initialPartners && initialPartners.length > 0);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/partners');
      if (!response.ok) {
        throw new Error('Failed to fetch partners');
      }

      const data = await response.json();
      if (data.success) {
        setPartners(data.partners);
        setHasLoaded(true);
      } else {
        throw new Error(data.error || 'Failed to load partners');
      }
    } catch (err) {
      trackError(err instanceof Error ? err : new Error('Error fetching partners'));
      setError(errorMessageOr(err, 'Failed to load partners'));
    } finally {
      setLoading(false);
    }
  };

  const ensureLoaded = async () => {
    // Only fetch if we haven't loaded yet and aren't currently loading
    if (!hasLoaded && !loading) {
      await fetchPartners();
    }
  };

  const refreshPartners = async () => {
    await fetchPartners();
  };

  const value: PartnersDataContextType = {
    partners,
    loading,
    error,
    refreshPartners,
    ensureLoaded,
  };

  return <PartnersDataContext.Provider value={value}>{children}</PartnersDataContext.Provider>;
};

export const usePartnersData = (): PartnersDataContextType => {
  const context = useContext(PartnersDataContext);
  if (context === undefined) {
    throw new Error('usePartnersData must be used within a PartnersDataProvider');
  }
  return context;
};

export default PartnersDataProvider;
